import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import {
    BoolArrayFilter,
    FilterClear,
    FilterGroup,
    HeaderFilter,
    Input,
    IsHighlightedFilter,
    IsNotificationReadFilter,
    NotificationSource,
    NotificationSourceFilter,
    useDebounced,
    useGetFilterPreferencesQuery,
    useOutsideClick,
    useUpdateFilterPreferences,
} from "modules/common";
import { DateRangePicker } from "modules/common/components/DateRangePicker/DateRangePicker";
import { mapToFilterName, UserNotificationsFilter } from "modules/common/utils/filters";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { UpdateFilterPreferencesRequest } from "services";

const INPUT_DEBOUNCE_TIME = 500;

// TODO: add date range when new date picker component is implemented
interface Props {
    searchFilter: string;
    setSearchFilter: React.Dispatch<React.SetStateAction<string>>;
    isHighlightedFilter: boolean;
    setIsHighlightedFilter: (value: React.SetStateAction<boolean>) => void;
    sourceFilter: NotificationSource[];
    startDate?: Date | null | undefined;
    setStartDate: (value: React.SetStateAction<Date | null | undefined>) => void;
    endDate?: Date | null | undefined;
    setEndDate: (value: React.SetStateAction<Date | null | undefined>) => void;
    setSourceFilter: (value: React.SetStateAction<NotificationSource[]>) => void;
    isReadFilter: BoolArrayFilter[];
    from: Date | null;
    setFrom: (value: React.SetStateAction<Date | null>) => void;
    to: Date | null;
    setTo: (value: React.SetStateAction<Date | null>) => void;
    setIsReadFilter: (value: React.SetStateAction<BoolArrayFilter[]>) => void;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

export const UserNotificationsFilters = forwardRef<{ clearFilters: () => void }, Props>(
    (
        {
            searchFilter,
            setSearchFilter,
            isHighlightedFilter,
            setIsHighlightedFilter,
            sourceFilter,
            setStartDate,
            setEndDate,
            endDate,
            startDate,
            setSourceFilter,
            isReadFilter,
            from,
            setFrom,
            to,
            setTo,
            setIsReadFilter,
            setCurrentPage,
        },
        ref
    ) => {
        // used to invoke clearFilter method to refetch newest data
        useImperativeHandle(ref, () => ({ clearFilters: () => handleClearFilter() }));

        const [search, setSearch] = useState(searchFilter);
        const debouncedSearch = useDebounced(search, INPUT_DEBOUNCE_TIME);

        useEffect(() => {
            setSearchFilter(debouncedSearch);
            setCurrentPage(1);
        }, [debouncedSearch]);

        // dropdown states
        const [showSourceFilter, setShowSourceFilter] = useState(false);
        const [showIsReadFilter, setShowIsReadFilter] = useState(false);

        // refs
        const isHighlightedFilterRef = useRef<HTMLDivElement>(null);
        const sourceFilterRef = useRef<HTMLDivElement>(null);
        const isReadFilterRef = useRef<HTMLDivElement>(null);

        // outside click
        const handleSourceFilterOutsideClick = () => setShowSourceFilter(false);
        const handleIsReadFilterOutsideClick = () => setShowIsReadFilter(false);
        useOutsideClick(sourceFilterRef, handleSourceFilterOutsideClick);
        useOutsideClick(isReadFilterRef, handleIsReadFilterOutsideClick);

        const filterPreferencesQuery = useGetFilterPreferencesQuery([
            IS_HIGHLIGHTED_FILTER_NAME,
            SOURCE_FILTER_NAME,
            IS_READ_FILTER_NAME,
        ]);

        // query
        const updateFilterPreferencesMutation = useUpdateFilterPreferences();

        useEffect(() => {
            if (filterPreferencesQuery.some(s => s.isLoading)) return;

            const isHighlightedFilterData = filterPreferencesQuery.find(
                fp => fp.data?.filterName === IS_HIGHLIGHTED_FILTER_NAME
            )?.data?.filterBoolValue;

            const sourceFilterData = filterPreferencesQuery.find(
                fp => fp.data?.filterName === SOURCE_FILTER_NAME
            )?.data?.filterIntValues;

            const isReadFilterData = filterPreferencesQuery.find(
                fp => fp.data?.filterName === IS_READ_FILTER_NAME
            )?.data?.filterIntValues;

            setIsHighlightedFilter(isHighlightedFilterData ?? false);

            if (sourceFilterData) setSourceFilter(sourceFilterData);

            if (isReadFilterData) setIsReadFilter(isReadFilterData);
        }, [filterPreferencesQuery]);

        // handle filter change based on filter type
        const handleUpdateFilterPreferences = (
            filterName: string,
            filterIntValue?: number,
            filterStringValue?: string,
            filterBoolValue?: boolean,
            clearFilter?: boolean
        ) => {
            const payload: UpdateFilterPreferencesRequest = {
                filterName,
                ...(filterIntValue !== undefined && { filterIntValue }),
                ...(filterStringValue !== undefined && { filterStringValue }),
                ...(filterBoolValue !== undefined && { filterBoolValue }),
                ...(clearFilter !== undefined && { clearFilter }),
            };
            updateFilterPreferencesMutation.mutate(payload);
            setCurrentPage(1);
        };

        // handle filter change
        const handleIsHighlightedFilterChange = (isHighlighted: boolean) =>
            handleUpdateFilterPreferences(
                IS_HIGHLIGHTED_FILTER_NAME,
                undefined,
                undefined,
                isHighlighted ?? undefined
            );
        const handleSourceFilterChange = (source: NotificationSource) =>
            handleUpdateFilterPreferences(SOURCE_FILTER_NAME, source);
        const handleIsReadFilterChange = (isRead: BoolArrayFilter) =>
            handleUpdateFilterPreferences(IS_READ_FILTER_NAME, isRead);
        const handleSearchFilterChange = (event: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(event.target.value);
        const handleDateFilterChange = (day: Date | null) => {
            if (!startDate && !endDate) {
                setStartDate(day)
                setFrom(day);
            } else if (startDate && !endDate) {
                setEndDate(day);
                setTo(day);
            } else if (startDate && endDate) {
                setStartDate(day);
                setFrom(day)
                setEndDate(null);
                setTo(null);
            }
        
        setCurrentPage(1);
    };

        // handle filter clear
        const handleIsHighlightedFilterClear = () =>
            handleUpdateFilterPreferences(
                IS_HIGHLIGHTED_FILTER_NAME,
                undefined,
                undefined,
                undefined,
                true
            );
        const handleSourceFilterClear = () =>
            handleUpdateFilterPreferences(
                SOURCE_FILTER_NAME,
                undefined,
                undefined,
                undefined,
                true
            );
        const handleIsReadFilterClear = () =>
            handleUpdateFilterPreferences(
                IS_READ_FILTER_NAME,
                undefined,
                undefined,
                undefined,
                true
            );
        const handleClearFilter = () => {
            setSearch("");
            setFrom(null);
            handleIsHighlightedFilterClear();
            handleSourceFilterClear();
            handleIsReadFilterClear();
        };

        // handle click
        const handleIsHighlightedFilterClick = () =>
            !isHighlightedFilter && handleIsHighlightedFilterChange(!isHighlightedFilter);
        const handleSourceFilterClick = () => setShowSourceFilter(prev => !prev);
        const handleIsReadFilterClick = () => setShowIsReadFilter(prev => !prev);

        return (
            <div className="f-r g-12 even">
                <Input
                    id="user-notifications-search"
                    name="user-notifications-search"
                    type="text"
                    size="medium"
                    placeholder="Pretraži poruke."
                    onChange={handleSearchFilterChange}
                    value={search}
                    leftIcon={<SearchIcon className="search-icon" />}
                />

                <HeaderFilter name="user-notifications" title="MOJE PORUKE">
                    <FilterGroup>
                        <div ref={isHighlightedFilterRef}>
                            <IsHighlightedFilter
                                onClick={handleIsHighlightedFilterClick}
                                onClear={handleIsHighlightedFilterClear}
                                isActive={isHighlightedFilter}
                            />
                        </div>

                        <div ref={sourceFilterRef}>
                            <NotificationSourceFilter
                                onChange={handleSourceFilterChange}
                                onClick={handleSourceFilterClick}
                                onClear={handleSourceFilterClear}
                                isActive={Boolean(sourceFilter.length)}
                                showDropdown={showSourceFilter}
                                value={sourceFilter}
                            />
                        </div>

                        <div ref={isReadFilterRef}>
                            <IsNotificationReadFilter
                                onChange={handleIsReadFilterChange}
                                onClick={handleIsReadFilterClick}
                                onClear={handleIsReadFilterClear}
                                isActive={Boolean(isReadFilter.length)}
                                showDropdown={showIsReadFilter}
                                value={isReadFilter}
                            />
                        </div>

                        <FilterClear
                            show={Boolean(
                                search.length ||
                                isHighlightedFilter ||
                                sourceFilter.length ||
                                isReadFilter.length ||
                                from
                            )}
                            onClearFilter={handleClearFilter}
                        />
                    </FilterGroup>
                </HeaderFilter>

                <DateRangePicker
                    onClick={(day) => handleDateFilterChange(day)}
                    value={{ from: startDate, to: endDate }}
                />
            </div>
        );
    }
);

// map filter names
const IS_HIGHLIGHTED_FILTER_NAME = mapToFilterName<UserNotificationsFilter>(
    UserNotificationsFilters.name,
    "isHighlightedFilter"
);
const SOURCE_FILTER_NAME = mapToFilterName<UserNotificationsFilter>(
    UserNotificationsFilters.name,
    "sourceFilter"
);
const IS_READ_FILTER_NAME = mapToFilterName<UserNotificationsFilter>(
    UserNotificationsFilters.name,
    "isReadFilter"
);
